/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Link as ReactRouterLink } from 'react-router';

import useRouter from 'js/lib/useRouter';

import type { LinkProps } from '@coursera/cds-core';
import { Link, Skeleton, Typography2, VisuallyHidden, breakpoints, useTheme } from '@coursera/cds-core';
import { SuccessIcon, VideoIcon } from '@coursera/cds-icons';
import { css as cuiCss, placeholder } from '@coursera/coursera-ui';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import getDurationFormatted from 'bundles/enterprise-legacy-learner-home/utils/getDurationFormatted';

import playNowIcon from 'images/clips/Vector.svg';

import _t from 'i18n!nls/clips';

export type Props = {
  lectureId?: string;
  courseSlug?: string;
  lectureName: string;
  lectureTimeCommitment: number;
  videoProgressMs?: number;
  isSelected?: boolean;
  isCompleted?: boolean | null;
  url?: string;
  trackingName: string;
};

const styles = {
  lectureItemContainer: () => css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: var(--cds-spacing-100);
    margin-top: var(--cds-spacing-200);
    margin-left: -20px;
    margin-right: -18px;
    padding-left: var(--cds-spacing-200);
    padding-top: var(--cds-spacing-50);
    padding-bottom: var(--cds-spacing-50);
    background: none;
    border-left: none;
    ${breakpoints.down('xs')} {
      padding-right: var(--cds-spacing-150);
      padding-left: var(--cds-spacing-200);
      margin-right: 0;
      margin-left: -16px;
    }
  `,
  isSelectedItem: () => css`
    background: var(--cds-color-grey-25);
    border-left: 4px solid var(--cds-color-blue-700);
    ${breakpoints.down('xs')} {
      padding-left: var(--cds-spacing-150);
    }
  `,
  lectureItemLoadingContainer: () => css`
    padding: 0;
    margin-left: 0;
    margin-right: 0;
    margin-top: var(--cds-spacing-200);
  `,
  overflowContainer: css`
    overflow: hidden;
  `,
  textTruncate: () => css`
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    ${breakpoints.down('xs')} {
      white-space: normal;
    }
  `,
  progressBarContainer: () => css`
    margin-top: var(--cds-spacing-100);
    background-color: var(--cds-color-grey-50);
    width: 144px;
    height: 2px;
  `,
  progressBar: () => css`
    position: relative;
    background-color: var(--cds-color-green-700);
    height: 100%;
  `,
};

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps<ReactRouterLink>>(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  Link
);

const LearnItemIcon = ({ isCompleted, isSelected }: { isCompleted?: boolean | null; isSelected?: boolean }) => {
  if (isSelected) {
    return <img src={playNowIcon} alt={_t('Playing now')} />;
  } else if (isCompleted) {
    return <SuccessIcon title={_t('Completed')} color="success" size="large" />;
  }

  return <VideoIcon size="large" color="default" />;
};

export const LectureItemLink = ({
  url,
  lectureId,
  courseSlug,
  lectureName,
  lectureTimeCommitment,
  videoProgressMs,
  isSelected,
  trackingName,
  isCompleted,
}: Props) => {
  const theme = useTheme();
  const router = useRouter();
  const {
    location: { query },
  } = router;

  // remove startAt from query
  const { startAt, ...restQuery } = query;
  let urlToUse = url ?? `/videos/${courseSlug}/${lectureId}`;
  const params: Record<string, string> = restQuery;
  if (!isCompleted && videoProgressMs) {
    // add startAt base on videoProgressMs
    params.startAt = (videoProgressMs / 1000).toString();
  }
  const qs = new URLSearchParams(params).toString();
  if (qs) {
    urlToUse += `?${qs}`;
  }

  const videoDuration = getDurationFormatted(lectureTimeCommitment);
  const videoTimeLeft = videoProgressMs ? getDurationFormatted(lectureTimeCommitment - videoProgressMs) : videoDuration;
  let videoDurationText;
  let videoDurationLabel;
  if (isSelected) {
    videoDurationText = _t('Playing now • #{videoTimeLeft} left', { videoTimeLeft });
    videoDurationLabel = _t('Playing now, #{videoTimeLeft} left', { videoTimeLeft });
  } else if (isCompleted) {
    videoDurationText = _t('Completed • #{videoDuration}', { videoDuration });
    videoDurationLabel = _t('Completed, #{videoDuration}', { videoDuration });
  } else if (!videoProgressMs) {
    // related videos
    videoDurationText = videoDuration;
    videoDurationLabel = videoDuration;
  } else {
    videoDurationText = _t('Continue playing • #{videoTimeLeft} left', { videoTimeLeft });
    videoDurationLabel = _t('Continue playing, #{videoDuration} total, #{videoTimeLeft} left', {
      videoDuration,
      videoTimeLeft,
    });
  }
  const showProgressBar = !isSelected && !isCompleted && Boolean(videoProgressMs);

  return (
    <TrackedLink
      to={urlToUse}
      variant="quiet"
      data-testid="lecture-item-link"
      component={ReactRouterLink}
      trackingName={trackingName}
    >
      <div css={[styles.lectureItemContainer, isSelected ? styles.isSelectedItem : {}]}>
        <div>
          <LearnItemIcon isSelected={isSelected} isCompleted={isCompleted} />
        </div>
        <div css={styles.overflowContainer}>
          <Typography2 variant="subtitleMedium" component="h4" css={styles.textTruncate}>
            {lectureName}
          </Typography2>
          <Typography2 component="p" variant="bodySecondary" color="supportText" aria-hidden={true}>
            {videoDurationText}
          </Typography2>
          <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
            {videoDurationLabel}
          </VisuallyHidden>
          {showProgressBar && (
            <div css={styles.progressBarContainer} aria-hidden={true}>
              <div
                css={styles.progressBar}
                style={{ width: `${((videoProgressMs || 0) * 100) / lectureTimeCommitment}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </TrackedLink>
  );
};

export const LectureItemLinkLoading = () => {
  return (
    <Skeleton
      css={styles.lectureItemLoadingContainer}
      className={cuiCss(placeholder.styles.shimmer).className}
      variant="rect"
      height={52}
    />
  );
};
